import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  HardHat, ArrowLeft, Star, CheckCircle, ThumbsUp,
  Wrench, MessageSquare, Clock, Award, DollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [review, setReview] = useState({
    overall_rating: 0,
    quality_rating: 0,
    communication_rating: 0,
    timeliness_rating: 0,
    professionalism_rating: 0,
    value_rating: 0,
    would_hire_again: null,
    review_text: ''
  });

  const handleRating = (field, value) => {
    setReview(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (review.overall_rating === 0) {
      alert('Please provide an overall rating');
      return;
    }
    if (review.would_hire_again === null) {
      alert('Please indicate if you would hire this contractor again');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        job_id: jobId,
        ...review
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit review:', error);
      alert(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ value, onChange, label, icon: Icon }) => (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-slate-400" />
          <span className="font-medium text-slate-700">{label}</span>
        </div>
        <span className="text-sm text-slate-500">
          {value > 0 ? `${value}/5` : 'Not rated'}
        </span>
      </div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`${label.toLowerCase().replace(/\s+/g, '-')}-star-${star}`}
          >
            <Star
              className={`w-8 h-8 ${
                star <= value 
                  ? 'text-orange-500 fill-orange-500' 
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-sm border border-slate-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-2">Thank You!</h2>
          <p className="text-slate-600 mb-6">
            Your review has been submitted successfully. It helps other homeowners make informed decisions.
          </p>
          <Link
            to={user.role === 'client' ? '/client/dashboard' : '/contractor/dashboard'}
            className="inline-block bg-orange-600 text-white px-6 py-3 rounded-sm font-bold"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">REVIEW</span>
            </div>
            <div className="w-16" />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black text-slate-900 mb-2">Rate Your Contractor</h1>
          <p className="text-slate-600">
            Help other homeowners by sharing your experience
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Overall Rating - Prominent */}
          <div className="bg-orange-50 border-2 border-orange-200 rounded-sm p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-slate-900">Overall Rating *</h3>
              <p className="text-sm text-slate-600">How would you rate your overall experience?</p>
            </div>
            <div className="flex justify-center gap-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRating('overall_rating', star)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`overall-star-${star}`}
                >
                  <Star
                    className={`w-12 h-12 ${
                      star <= review.overall_rating 
                        ? 'text-orange-500 fill-orange-500' 
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {review.overall_rating > 0 && (
              <p className="text-center mt-3 text-orange-700 font-medium">
                {review.overall_rating === 5 ? 'Excellent!' :
                 review.overall_rating === 4 ? 'Very Good' :
                 review.overall_rating === 3 ? 'Good' :
                 review.overall_rating === 2 ? 'Fair' : 'Poor'}
              </p>
            )}
          </div>

          {/* Detailed Ratings */}
          <div className="grid md:grid-cols-2 gap-4">
            <StarRating
              value={review.quality_rating}
              onChange={(v) => handleRating('quality_rating', v)}
              label="Quality of Work"
              icon={Wrench}
            />
            <StarRating
              value={review.communication_rating}
              onChange={(v) => handleRating('communication_rating', v)}
              label="Communication"
              icon={MessageSquare}
            />
            <StarRating
              value={review.timeliness_rating}
              onChange={(v) => handleRating('timeliness_rating', v)}
              label="Timeliness"
              icon={Clock}
            />
            <StarRating
              value={review.professionalism_rating}
              onChange={(v) => handleRating('professionalism_rating', v)}
              label="Professionalism"
              icon={Award}
            />
            <StarRating
              value={review.value_rating}
              onChange={(v) => handleRating('value_rating', v)}
              label="Value for Money"
              icon={DollarSign}
            />
          </div>

          {/* Would Hire Again */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-bold text-slate-900 mb-4">Would you hire this contractor again? *</h3>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setReview(prev => ({ ...prev, would_hire_again: true }))}
                className={`flex-1 p-4 rounded-sm border-2 flex items-center justify-center gap-2 transition-all ${
                  review.would_hire_again === true
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="would-hire-yes"
              >
                <ThumbsUp className={`w-5 h-5 ${review.would_hire_again === true ? 'text-green-600' : 'text-slate-400'}`} />
                <span className="font-bold">Yes, definitely!</span>
              </button>
              <button
                type="button"
                onClick={() => setReview(prev => ({ ...prev, would_hire_again: false }))}
                className={`flex-1 p-4 rounded-sm border-2 flex items-center justify-center gap-2 transition-all ${
                  review.would_hire_again === false
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="would-hire-no"
              >
                <ThumbsUp className={`w-5 h-5 rotate-180 ${review.would_hire_again === false ? 'text-red-600' : 'text-slate-400'}`} />
                <span className="font-bold">No</span>
              </button>
            </div>
          </div>

          {/* Written Review */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-bold text-slate-900 mb-2">Written Review (Optional)</h3>
            <p className="text-sm text-slate-500 mb-4">
              Share details about your experience (50-500 characters)
            </p>
            <textarea
              value={review.review_text}
              onChange={(e) => setReview(prev => ({ ...prev, review_text: e.target.value }))}
              placeholder="Tell others about your experience with this contractor..."
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 resize-none"
              data-testid="review-text"
            />
            <p className="text-xs text-slate-400 text-right mt-1">
              {review.review_text.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || review.overall_rating === 0 || review.would_hire_again === null}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-review-btn"
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>

          <p className="text-xs text-slate-500 text-center">
            Your review will be visible to other homeowners and the contractor.
            Please be honest and constructive.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ReviewPage;
